import cn from 'classnames'
import type { ReactNode } from 'react'

/**
 * Purpose-built illustrations in the site's ink-and-signal palette. Each one depicts
 * the promise of the section it sits in, instead of a generic stock scene. They are
 * plain JSX and Tailwind so they stay crisp at any size and follow the theme.
 */
export type IllustrationName =
  | 'no-box'
  | 'pairing'
  | 'browser'
  | 'devices'
  | 'widgets'
  | 'split'
  | 'schedule'
  | 'channels'
  | 'fleet'
  | 'support'
  | 'assistant'

export const ILLUSTRATION_NAMES: IllustrationName[] = [
  'no-box',
  'pairing',
  'browser',
  'devices',
  'widgets',
  'split',
  'schedule',
  'channels',
  'fleet',
  'support',
  'assistant',
]

export const isIllustrationName = (v: unknown): v is IllustrationName =>
  typeof v === 'string' && (ILLUSTRATION_NAMES as string[]).includes(v)

/** Reads the optional `illustration` key a slice's primary content may carry. */
export const illustrationOf = (primary: unknown): IllustrationName | undefined => {
  const v = (primary as { illustration?: unknown } | null | undefined)?.illustration
  return isIllustrationName(v) ? v : undefined
}

export const Illustration = ({ name, className }: { name: IllustrationName; className?: string }) => {
  const Comp = registry[name]
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative aspect-[4/3] w-full overflow-hidden rounded-[28px] bg-[#f6f3f8] ring-1 ring-black/5 select-none',
        className
      )}
    >
      <div className="ink-grid-light pointer-events-none absolute inset-0" />
      <Comp />
    </div>
  )
}

/* ---------- primitives ------------------------------------------------------ */

const Screen = ({
  className,
  children,
  stand = true,
}: {
  className?: string
  children: ReactNode
  stand?: boolean
}) => (
  <div className={cn('absolute', className)}>
    <div className="rounded-[14px] bg-[#120616] p-[3%] shadow-[0_30px_60px_-24px_rgba(18,6,22,0.55)] ring-1 ring-white/10">
      <div className="relative aspect-video overflow-hidden rounded-[8px] bg-[#1c0826]">{children}</div>
    </div>
    {stand && (
      <div className="mx-auto mt-[2%] h-[6%] w-[22%] rounded-b-md bg-gradient-to-b from-[#2a1436] to-[#120616]" />
    )}
  </div>
)

const Chip = ({
  children,
  tone = 'light',
  className,
}: {
  children: ReactNode
  tone?: 'light' | 'ink' | 'signal' | 'green'
  className?: string
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] leading-none font-semibold whitespace-nowrap shadow-sm',
      tone === 'light' && 'bg-white text-[#2d033b] ring-1 ring-black/5',
      tone === 'ink' && 'bg-[#120616] text-white',
      tone === 'signal' && 'bg-[#810ca8] text-white',
      tone === 'green' && 'bg-[#e3f5ea] text-[#1f7a45]',
      className
    )}
  >
    {children}
  </span>
)

const Dot = ({ color = '#1f9d55' }: { color?: string }) => (
  <span className="inline-block size-1.5 rounded-full" style={{ background: color }} />
)

const Card = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div
    className={cn(
      'absolute rounded-2xl bg-white p-3 shadow-[0_20px_50px_-24px_rgba(18,6,22,0.45)] ring-1 ring-black/5',
      className
    )}
  >
    {children}
  </div>
)

const Lines = ({ n = 3, className }: { n?: number; className?: string }) => (
  <div className={cn('space-y-1.5', className)}>
    {Array.from({ length: n }).map((_, i) => (
      <div
        key={`l${n}-${i + 1}`}
        className="h-1.5 rounded bg-current opacity-15"
        style={{ width: `${88 - i * 18}%` }}
      />
    ))}
  </div>
)

/* Fake menu-board content used on several screens. */
const MenuBoard = ({ compact = false }: { compact?: boolean }) => (
  <div className="absolute inset-0 flex bg-gradient-to-br from-[#1e2f3a] to-[#0f1a22] p-[5%] text-white">
    <div className="flex-1">
      <p className="truncate text-[8px] font-semibold tracking-[0.14em] text-white/60 uppercase">
        Lunch · 11–14
      </p>
      <p className={cn('mt-1 leading-tight font-semibold', compact ? 'text-[11px]' : 'text-[15px]')}>
        Soup of the day
      </p>
      {!compact && <p className="text-[10px] text-white/70">with sourdough</p>}
      <div className={cn('mt-2 space-y-1 text-[9px] text-white/80', compact && 'hidden')}>
        <div className="flex justify-between">
          <span>Flat white</span>
          <span>3.90</span>
        </div>
        <div className="flex justify-between">
          <span>Cinnamon bun</span>
          <span>2.60</span>
        </div>
        <div className="flex justify-between">
          <span>Toastie</span>
          <span>6.50</span>
        </div>
      </div>
    </div>
    <div className="ml-[4%] w-[34%] rounded-md bg-gradient-to-br from-[#c8915a] to-[#8a5a2b]" />
  </div>
)

/* ---------- illustrations ---------------------------------------------------- */

/** Native stability: the app runs on the panel; the media player box is gone. */
const NoBox = () => (
  <>
    <Screen className="top-[12%] left-[7%] w-[62%]">
      <MenuBoard />
      <Chip tone="signal" className="absolute top-[6%] left-[5%] !text-[9px]">
        <Dot color="#fff" /> Signital app · on the panel
      </Chip>
    </Screen>
    <div className="absolute top-[10%] right-[6%] flex flex-col items-end gap-2">
      <Chip>Samsung Tizen</Chip>
      <Chip>LG webOS</Chip>
      <Chip>Android TV</Chip>
    </div>
    <Card className="right-[7%] bottom-[10%] w-[34%] !bg-[#f6f3f8] !p-3">
      <div className="relative h-8 rounded-md border border-dashed border-[#9a8aa3]/70 bg-white/60">
        <span className="absolute top-1/2 left-2 flex -translate-y-1/2 gap-1">
          <span className="size-1.5 rounded-full bg-[#9a8aa3]/60" />
          <span className="size-1.5 rounded-full bg-[#9a8aa3]/60" />
        </span>
        <span className="absolute inset-x-[-6%] top-1/2 h-[2px] -rotate-6 rounded bg-[#d64545]" />
      </div>
      <p className="mt-2 text-[11px] leading-4 font-semibold text-[#2d033b]">No media player</p>
      <p className="text-[10px] leading-4 text-[#6b6b6b]">Nothing to buy, mount or replace.</p>
    </Card>
  </>
)

const CodeCells = ({ code = 'K7M2X', size = 'md' }: { code?: string; size?: 'sm' | 'md' }) => (
  <div className="flex gap-1">
    {code.split('').map((c, i) => (
      <span
        key={`${c}${i + 1}`}
        className={cn(
          'flex items-center justify-center rounded-md bg-white font-mono font-bold text-[#2d033b] ring-1 ring-black/10',
          size === 'md' ? 'h-8 w-6 text-[14px]' : 'h-5 w-4 text-[9px]'
        )}
      >
        {c}
      </span>
    ))}
  </div>
)

const Qr = ({ className }: { className?: string }) => {
  const cells = ['1111101', '1000101', '1011101', '1000100', '1111111', '0101010', '1110011']
  const dots = cells.flatMap((row, r) => row.split('').map((v, c) => ({ id: `q${r}${c}`, on: v === '1' })))
  return (
    <div className={cn('grid grid-cols-7 gap-[1px] rounded-sm bg-white p-1', className)}>
      {dots.map((d) => (
        <span key={d.id} className={cn('aspect-square', d.on ? 'bg-[#120616]' : 'bg-white')} />
      ))}
    </div>
  )
}

/** Getting started: the screen shows a code and QR; the phone confirms it is online. */
const Pairing = () => (
  <>
    <Screen className="top-[14%] left-[6%] w-[60%]">
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#120616] text-white">
        <p className="text-[9px] tracking-[0.18em] text-white/60 uppercase">Connect this screen</p>
        <CodeCells />
        <div className="flex items-center gap-2 text-[9px] text-white/70">
          <Qr className="w-9" /> or scan
        </div>
      </div>
    </Screen>
    <svg
      aria-hidden="true"
      className="absolute top-[46%] left-[62%] w-[16%] text-[#c147e9]"
      viewBox="0 0 100 40"
      fill="none"
    >
      <path
        d="M2 30 C 40 30, 60 8, 98 8"
        stroke="currentColor"
        strokeWidth="3"
        strokeDasharray="6 6"
        strokeLinecap="round"
      />
    </svg>
    <div className="absolute top-[22%] right-[9%] w-[22%] rounded-[18px] bg-[#120616] p-[4%] shadow-[0_30px_60px_-24px_rgba(18,6,22,0.6)]">
      <div className="flex aspect-[9/17] flex-col rounded-[12px] bg-white p-2.5">
        <div className="mx-auto mb-2 h-1 w-1/3 rounded bg-black/10" />
        <p className="text-[8px] font-semibold text-[#6b6b6b] uppercase">Connect display</p>
        <p className="text-[11px] font-semibold text-[#2d033b]">Entrance screen</p>
        <div className="mt-2 rounded-lg bg-[#f6f3f8] p-2">
          <CodeCells size="sm" />
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="mt-2 rounded-full bg-[#810ca8] py-1.5 text-[9px] font-semibold text-white"
        >
          Connect
        </button>
        <div className="mt-auto flex items-center justify-center">
          <Chip tone="green">
            <Dot /> Online
          </Chip>
        </div>
      </div>
    </div>
    <div className="absolute bottom-[9%] left-[6%] flex gap-2">
      <Chip>Samsung Tizen</Chip>
      <Chip>LG webOS</Chip>
      <Chip>Fire TV</Chip>
      <Chip>Any browser</Chip>
    </div>
  </>
)

/** Web player: a browser at signital.tv is a display. */
const Browser = () => (
  <>
    <div className="absolute top-[12%] left-[8%] w-[84%] overflow-hidden rounded-xl bg-white shadow-[0_30px_60px_-24px_rgba(18,6,22,0.5)] ring-1 ring-black/10">
      <div className="flex items-center gap-1.5 border-b border-black/5 bg-[#f6f3f8] px-3 py-2">
        <span className="size-2 rounded-full bg-[#ff5f57]" />
        <span className="size-2 rounded-full bg-[#febc2e]" />
        <span className="size-2 rounded-full bg-[#28c840]" />
        <span className="mx-auto rounded-md bg-white px-10 py-0.5 text-[10px] text-black/60 ring-1 ring-black/5">
          signital.tv
        </span>
      </div>
      <div className="relative aspect-[16/8] bg-[#120616]">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
          <p className="text-[9px] tracking-[0.18em] text-white/60 uppercase">Connect this screen</p>
          <CodeCells code="R4WQ8" />
          <p className="text-[9px] text-white/60">Nothing to install</p>
        </div>
        <Qr className="absolute right-4 bottom-4 w-12" />
      </div>
    </div>
    <div className="absolute bottom-[8%] left-[8%] flex gap-2">
      <Chip>Chrome</Chip>
      <Chip>Edge</Chip>
      <Chip>Safari</Chip>
      <Chip>TV browsers</Chip>
    </div>
  </>
)

const DeviceCard = ({
  className,
  label,
  sub,
  children,
}: {
  className?: string
  label: string
  sub: string
  children: ReactNode
}) => (
  <Card className={cn('w-[27%] !p-2.5', className)}>
    <div className="flex aspect-[16/9] items-center justify-center rounded-lg bg-[#f6f3f8]">{children}</div>
    <div className="mt-2 flex items-center justify-between">
      <div>
        <p className="text-[11px] leading-4 font-semibold text-[#2d033b]">{label}</p>
        <p className="text-[9px] leading-3 text-[#6b6b6b]">{sub}</p>
      </div>
      <Dot />
    </div>
  </Card>
)

/** Players: every kind of hardware, all showing the same content. */
const Devices = () => (
  <>
    <Screen className="top-[34%] left-[36%] w-[28%]" stand={false}>
      <MenuBoard compact />
    </Screen>
    <DeviceCard className="top-[7%] left-[4%]" label="Smart TV" sub="Tizen · webOS">
      <div className="h-[55%] w-[70%] rounded-sm bg-[#120616]" />
    </DeviceCard>
    <DeviceCard className="top-[7%] right-[4%]" label="Fire TV Stick" sub="Amazon Appstore">
      <div className="h-[22%] w-[60%] rounded-full bg-[#120616]" />
    </DeviceCard>
    <DeviceCard className="bottom-[7%] left-[4%]" label="Raspberry Pi" sub="Signital OS image">
      <div className="grid h-[55%] w-[62%] grid-cols-6 gap-[2px] rounded-sm bg-[#1f7a45] p-1">
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={`pin${i + 1}`} className="rounded-[1px] bg-[#e3f5ea]/70" />
        ))}
      </div>
    </DeviceCard>
    <DeviceCard className="right-[4%] bottom-[7%]" label="BrightSign" sub="LS · HD · XD">
      <div className="h-[45%] w-[60%] rounded-md bg-[#2b1b33]" />
    </DeviceCard>
    <Chip tone="ink" className="absolute bottom-[8%] left-1/2 -translate-x-1/2">
      One plan · every device
    </Chip>
  </>
)

/** Widgets: live data laid out on one screen. */
const Widgets = () => (
  <>
    <Screen className="top-[10%] left-[6%] w-[60%]">
      <div className="absolute inset-0 grid w-full grid-cols-[repeat(3,minmax(0,1fr))] grid-rows-[1fr_1fr_auto] gap-1 overflow-hidden bg-[#0f0a14] p-1 *:min-w-0">
        <div className="col-span-2 row-span-2 rounded-md bg-gradient-to-br from-[#4a1a63] to-[#2a0f3a] p-2 text-white">
          <p className="text-[8px] tracking-[0.18em] text-white/60 uppercase">Weather · Today</p>
          <p className="mt-1 text-[22px] leading-none font-semibold">7°</p>
          <p className="text-[9px] text-white/70">Light rain · wind 6 m/s</p>
          <div className="mt-2 flex gap-1">
            {[6, 8, 9, 7, 5].map((t, i) => (
              <span key={`t${i + 1}`} className="rounded bg-white/10 px-1.5 py-0.5 text-[8px]">
                {t}°
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-md bg-white p-1.5">
          <p className="text-[7px] font-semibold text-[#6b6b6b] uppercase">Clock</p>
          <p className="text-[14px] leading-none font-semibold text-[#2d033b]">11:42</p>
        </div>
        <div className="rounded-md bg-white p-1.5">
          <p className="text-[7px] font-semibold text-[#6b6b6b] uppercase">Calendar</p>
          <p className="text-[8px] leading-3 font-semibold text-[#2d033b]">12:00 Stand-up</p>
          <p className="text-[8px] leading-3 text-[#6b6b6b]">14:00 Supplier call</p>
        </div>
        <div className="col-span-3 flex min-w-0 items-center overflow-hidden rounded-md bg-[#c147e9] px-2 py-1">
          <span className="animate-marquee w-max text-[8px] font-medium whitespace-nowrap text-white">
            News · Markets open higher · Roadworks on the ring road until Friday · News · Markets open higher
            · Roadworks on the ring road until Friday ·
          </span>
        </div>
      </div>
    </Screen>
    <div className="absolute bottom-[9%] left-[6%] flex w-[70%] flex-wrap gap-2">
      <Chip>Power BI</Chip>
      <Chip>Google Calendar</Chip>
      <Chip>RSS · News</Chip>
      <Chip>YouTube</Chip>
    </div>
    <Chip tone="ink" className="absolute top-[12%] right-[6%]">
      Updates itself · no code
    </Chip>
  </>
)

/** Features: split-screen sections, each with its own plan. */
const Split = () => (
  <>
    <Screen className="top-[10%] left-[6%] w-[60%]">
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-4 gap-1 bg-[#0f0a14] p-1 text-[8px] font-semibold">
        <div className="col-span-3 row-span-3 flex items-end rounded-md bg-gradient-to-br from-[#4a1a63] to-[#2a0f3a] p-2 text-white/90">
          Main · 1440×810
        </div>
        <div className="col-span-1 row-span-3 flex items-end rounded-md bg-white p-2 text-[#2d033b]">
          Sidebar
        </div>
        <div className="col-span-4 flex items-center rounded-md bg-[#c147e9] px-2 text-white">
          Footer ticker
        </div>
      </div>
    </Screen>
    <Card className="right-[5%] bottom-[10%] w-[30%]">
      <p className="text-[9px] font-semibold text-[#6b6b6b] uppercase">Choose layout</p>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {[
          ['3fr 1fr', '1fr', 'auto'],
          ['1fr', '1fr', 'none'],
          ['1fr 1fr', '1fr', 'none'],
          ['1fr', '1fr 1fr', 'none'],
        ].map(([cols, rows], i) => (
          <div
            key={`lay${i + 1}`}
            className={cn(
              'grid aspect-video gap-[2px] rounded-sm p-[2px]',
              i === 0 ? 'bg-[#810ca8]' : 'bg-[#e9e2ee]'
            )}
            style={{ gridTemplateColumns: cols, gridTemplateRows: rows }}
          >
            <span className="rounded-[1px] bg-white/80" />
            <span className="rounded-[1px] bg-white/80" />
            {i === 0 && <span className="col-span-2 rounded-[1px] bg-white/80" />}
          </div>
        ))}
      </div>
    </Card>
    <Chip tone="ink" className="absolute top-[10%] right-[6%]">
      16 stock layouts
    </Chip>
  </>
)

/** Management: a week of scheduled items, and who may publish. */
const Schedule = () => {
  const days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
  return (
    <>
      <Card className="top-[10%] left-[6%] w-[68%] !p-4">
        <div className="flex items-baseline justify-between">
          <p className="text-[11px] font-semibold text-[#2d033b]">Café menu · this week</p>
          <Chip tone="signal" className="!text-[9px]">
            Publish changes
          </Chip>
        </div>
        <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[9px] font-semibold text-[#6b6b6b]">
          {days.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {days.map((d, i) => (
            <div key={`col-${d}`} className="flex h-20 flex-col gap-1 rounded-md bg-[#f6f3f8] p-1">
              <span className="h-3 rounded-sm bg-[#c9b4d6]" />
              {i < 5 && <span className="h-6 rounded-sm bg-[#810ca8]" />}
              {i >= 5 && <span className="h-4 rounded-sm bg-[#f0b35c]" />}
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[9px]">
          <span className="inline-flex items-center gap-1 text-[#4b4b4b]">
            <span className="size-2 rounded-sm bg-[#c9b4d6]" /> Promo · always
          </span>
          <span className="inline-flex items-center gap-1 text-[#4b4b4b]">
            <span className="size-2 rounded-sm bg-[#810ca8]" /> Lunch 11–14 · take over
          </span>
          <span className="inline-flex items-center gap-1 text-[#4b4b4b]">
            <span className="size-2 rounded-sm bg-[#f0b35c]" /> Brunch
          </span>
        </div>
      </Card>
      <Card className="right-[5%] bottom-[10%] w-[30%]">
        <p className="text-[9px] font-semibold text-[#6b6b6b] uppercase">Team</p>
        <ul className="mt-2 space-y-1.5 text-[10px]">
          {[
            ['AB', 'Owner'],
            ['JK', 'Admin'],
            ['SM', 'Editor'],
            ['LP', 'Viewer'],
          ].map(([ini, role]) => (
            <li key={role} className="flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded-full bg-[#810ca8] text-[8px] font-bold text-white">
                {ini}
              </span>
              <span className="text-[#2d033b]">{role}</span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  )
}

/** Channels: one plan, many screens. */
const Channels = () => (
  <>
    <Card className="top-[36%] left-[5%] w-[30%]">
      <p className="text-[9px] font-semibold text-[#810ca8] uppercase">Channel</p>
      <p className="text-[12px] font-semibold text-[#2d033b]">Café menu</p>
      <Lines n={3} className="mt-2 text-[#2d033b]" />
      <Chip tone="signal" className="mt-3 !text-[9px]">
        Publish changes
      </Chip>
    </Card>
    <svg
      aria-hidden="true"
      className="absolute top-[12%] left-[34%] h-[76%] w-[22%] text-[#c147e9]"
      viewBox="0 0 100 300"
      fill="none"
      preserveAspectRatio="none"
    >
      {[40, 150, 260].map((y) => (
        <path
          key={y}
          d={`M0 150 C 50 150, 50 ${y}, 100 ${y}`}
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray="7 7"
          strokeLinecap="round"
        />
      ))}
    </svg>
    {[
      ['top-[5%]', 'Entrance'],
      ['top-[37%]', 'Menu board left'],
      ['top-[69%]', 'Menu board right'],
    ].map(([pos, label]) => (
      <div key={label} className={cn('absolute right-[8%] w-[30%]', pos)}>
        <Screen className="relative w-full" stand={false}>
          <MenuBoard compact />
        </Screen>
        <div className="mt-1 flex items-center justify-between px-1 text-[9px] font-semibold text-[#2d033b]">
          {label}
          <Chip tone="green" className="!px-2 !py-0.5 !text-[8px]">
            <Dot /> Online
          </Chip>
        </div>
      </div>
    ))}
  </>
)

/** Fleet: health at a glance across locations. */
const Fleet = () => {
  const r = 34
  const c = 2 * Math.PI * r
  return (
    <>
      <Card className="top-[12%] left-[6%] w-[52%] !p-4">
        <p className="text-[9px] font-semibold text-[#810ca8] uppercase">Fleet health</p>
        <div className="mt-2 flex items-center gap-4">
          <svg aria-hidden="true" viewBox="0 0 80 80" className="w-[38%]">
            <circle cx="40" cy="40" r={r} stroke="#e9e2ee" strokeWidth="9" fill="none" />
            <circle
              cx="40"
              cy="40"
              r={r}
              stroke="#1f9d55"
              strokeWidth="9"
              fill="none"
              strokeDasharray={`${c * 0.96} ${c}`}
              strokeLinecap="round"
              transform="rotate(-90 40 40)"
            />
            <text x="40" y="44" textAnchor="middle" fontSize="16" fontWeight="700" fill="#120616">
              96%
            </text>
          </svg>
          <ul className="space-y-1.5 text-[10px] text-[#2d033b]">
            <li className="flex items-center gap-1.5">
              <Dot /> <b>132</b> online
            </li>
            <li className="flex items-center gap-1.5">
              <Dot color="#d64545" /> <b>5</b> offline
            </li>
            <li className="flex items-center gap-1.5">
              <Dot color="#c9c9c9" /> <b>3</b> not connected
            </li>
          </ul>
        </div>
      </Card>
      <Card className="top-[12%] right-[6%] w-[32%]">
        <p className="text-[9px] font-semibold text-[#d64545] uppercase">Needs attention</p>
        <ul className="mt-2 space-y-1.5 text-[10px]">
          {[
            ['Back office', '3d'],
            ['Window left', '40m'],
          ].map(([n, t]) => (
            <li key={n} className="flex items-center justify-between text-[#2d033b]">
              <span className="flex items-center gap-1.5">
                <Dot color="#d64545" /> {n}
              </span>
              <span className="text-[#6b6b6b]">{t}</span>
            </li>
          ))}
        </ul>
      </Card>
      <div className="absolute inset-x-[6%] bottom-[14%] grid grid-cols-3 gap-2">
        {[
          ['Downtown', 12, 100],
          ['Airport', 8, 88],
          ['Harbour', 6, 100],
        ].map(([name, n, pct]) => (
          <div key={String(name)} className="rounded-xl bg-white p-2.5 shadow-sm ring-1 ring-black/5">
            <div className="flex justify-between text-[10px] font-semibold text-[#2d033b]">
              <span>{name}</span>
              <span className="text-[#6b6b6b]">{n} screens</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-[#f1dede]">
              <div className="h-1.5 rounded-full bg-[#1f9d55]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

/** Support: docs, a person, and a screen that came back. */
const Support = () => (
  <>
    <Card className="top-[12%] left-[6%] w-[44%]">
      <p className="text-[9px] font-semibold text-[#810ca8] uppercase">Help center</p>
      <p className="mt-1 text-[11px] font-semibold text-[#2d033b]">Player guides</p>
      <ul className="mt-2 space-y-1 text-[10px] text-[#4b4b4b]">
        {['Samsung Tizen', 'LG webOS', 'Amazon Fire TV', 'Raspberry Pi'].map((g) => (
          <li key={g} className="flex items-center justify-between rounded-md bg-[#f6f3f8] px-2 py-1">
            {g} <span className="text-[#810ca8]">›</span>
          </li>
        ))}
      </ul>
    </Card>
    <div className="absolute top-[14%] right-[6%] w-[42%] space-y-2">
      <div className="ml-6 rounded-2xl rounded-tr-sm bg-[#810ca8] p-2.5 text-[10px] leading-4 text-white shadow-md">
        Our menu board in the back office went dark this morning.
      </div>
      <div className="mr-6 rounded-2xl rounded-tl-sm bg-white p-2.5 text-[10px] leading-4 text-[#2d033b] shadow-md ring-1 ring-black/5">
        Restarted it from the portal. It is back online and playing the lunch menu.
      </div>
      <div className="mr-6 flex justify-start">
        <Chip tone="green">
          <Dot /> Back office · Online
        </Chip>
      </div>
    </div>
    <div className="absolute bottom-[9%] left-[6%] flex gap-2">
      <Chip>support@signital.com</Chip>
      <Chip>docs.signital.com</Chip>
    </div>
  </>
)

/** Assistant: a question answered from the account, a change waiting for a click, and outside AI apps. */
const Assistant = () => (
  <>
    <div className="absolute top-[10%] left-[6%] w-[46%] overflow-hidden rounded-2xl bg-white shadow-[0_20px_50px_-24px_rgba(18,6,22,0.45)] ring-1 ring-black/5">
      <div className="bg-[#2d033b] px-3 py-2 text-[10px] font-semibold text-white">Signital Assistant</div>
      <div className="space-y-2 p-2.5">
        <div className="ml-5 rounded-2xl rounded-tr-sm bg-[#810ca8] p-2 text-[10px] leading-4 text-white">
          Why is the lobby screen black?
        </div>
        <div className="mr-3 rounded-2xl rounded-tl-sm bg-[#f6f3f8] p-2 text-[10px] leading-4 text-[#2d033b]">
          Its only item ended yesterday. Shall I add the new welcome loop and publish?
        </div>
        <Chip tone="green">
          <Dot /> Lobby · Online
        </Chip>
      </div>
    </div>
    <Card className="top-[16%] right-[6%] w-[40%]">
      <p className="text-[9px] font-semibold text-[#810ca8] uppercase">Proposed change</p>
      <p className="mt-1 text-[11px] font-semibold text-[#2d033b]">Publish “Lobby”</p>
      <ul className="mt-2 space-y-1 text-[10px] text-[#4b4b4b]">
        {['Lobby left', 'Lobby right', 'Entrance'].map((n) => (
          <li key={n} className="flex items-center gap-1.5">
            <Dot /> {n}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-1.5 text-[9px] text-[#4b4b4b]">
        <span className="flex size-3 items-center justify-center rounded-[3px] bg-[#810ca8] text-[8px] leading-none text-white">
          ✓
        </span>
        I understand this changes what 3 screens show
      </div>
      <div className="mt-2 rounded-lg bg-[#810ca8] py-1.5 text-center text-[10px] font-semibold text-white">
        Publish to 3 screens
      </div>
    </Card>
    <div className="absolute bottom-[9%] left-[6%] hidden flex-wrap gap-2 sm:flex">
      <Chip tone="ink">Claude · Publish</Chip>
      <Chip>ChatGPT · Read</Chip>
      <Chip>Connected apps</Chip>
    </div>
  </>
)

const registry: Record<IllustrationName, () => ReactNode> = {
  'no-box': NoBox,
  pairing: Pairing,
  browser: Browser,
  devices: Devices,
  widgets: Widgets,
  split: Split,
  schedule: Schedule,
  channels: Channels,
  fleet: Fleet,
  support: Support,
  assistant: Assistant,
}

export default Illustration
